'use client';

import { useEffect } from 'react';

const CHUNK_RELOAD_KEY = 'targetqa_chunk_reload_ts';

function chunkUrlFrom(error: Error): string | null {
  const match = error.message?.match(/(\/_next\/static\/[^\s"']+\.(?:js|css))/i);
  return match?.[1] ?? null;
}

async function refreshChunkAndReload(error: Error) {
  const chunkUrl = chunkUrlFrom(error);

  if (chunkUrl) {
    try {
      // `reload` bypasses a cached negative response and replaces it with the
      // current immutable asset before the document is loaded again.
      await fetch(chunkUrl, { cache: 'reload', credentials: 'same-origin' });
    } catch {
      // A full reload still gives the deployment a chance to provide a newer
      // document whose asset URLs carry the new deployment identifier.
    }
  }

  window.location.reload();
}

/**
 * Root Error Boundary for TargetQA.
 * Catches client runtime errors and stale-chunk deployment errors,
 * providing automatic recovery and a clean branded UI.
 */
export default function GlobalError({
  error,
  retry,
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  const isChunkError =
    error.name === 'ChunkLoadError' ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Failed to load chunk') ||
    /chunk/i.test(error.message || '');

  useEffect(() => {
    if (isChunkError) {
      const lastReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      const now = Date.now();
      // Only auto-reload if not already reloaded in the last 15 seconds to prevent loops
      if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, now.toString());
        void refreshChunkAndReload(error);
      }
    }
  }, [error, isChunkError]);

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center p-6 text-center bg-[#0d120f] text-white">
      <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 mb-5 text-2xl font-bold">
        !
      </div>
      <h2 className="text-2xl font-bold tracking-tight text-white mb-2">
        {isChunkError ? 'A new update is available' : 'Something went wrong'}
      </h2>
      <p className="text-stone-400 text-sm max-w-md mb-8 leading-relaxed">
        {isChunkError
          ? 'TargetQA was just updated. Please reload the page to load the latest features and challenges.'
          : 'The page encountered an unexpected error. Try again, or reload the page if the problem continues.'}
      </p>
      <div className="flex gap-3">
        <button
          onClick={() => {
            sessionStorage.removeItem(CHUNK_RELOAD_KEY);
            const sep = window.location.search ? "&" : "?";
            window.location.href = window.location.pathname + window.location.search + sep + "_r=" + Date.now();
          }}
          className="px-6 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm transition-all shadow-lg shadow-emerald-500/20"
        >
          Reload Page
        </button>
        <button
          onClick={() => retry()}
          className="px-6 py-2.5 rounded-xl border border-white/10 hover:bg-white/5 text-stone-300 font-medium text-sm transition-all"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}
